import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, clearStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      // Stored token is invalid/expired — clear it so we don't keep sending it.
      clearStoredToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const refresh = checkAuth;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-blocking: local state still clears even if the server logout fails.
      if (typeof window !== "undefined" && window.console) {
        window.console.warn("[auth] logout request failed:", err?.message || err);
      }
    }
    setUser(null);
    clearStoredToken();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
